import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import { useDebounce } from 'hooks/useDebounce';

import { Icon } from 'components/Icon';
import { useHistory } from 'react-router-dom';

import '../style.scss';
import { clientApi } from 'helpers/api/client';
import cn from 'classnames';

const TableProp = ({ title, icon, required }) => {
  return (
    <div className="modal-create-prop">
      {title}
      {required && <span className="required">*</span>}
      <Icon name={icon} style={{ display: 'inline-block', margin: '0 5px' }} />
      {'  -  '}
    </div>
  );
};

TableProp.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

TableProp.defaultProps = {
  required: false,
};

const TableValueDynamic = ({
  err,
  value,
  onChange,
  mask,
  placeholder,
  autoFocus,
  styles,
  className,
  onFocus,
  onBlur,
}) => {
  const style = { ...styles };
  if (err) {
    style.border = '1px solid red';
  }
  const htmlElRef = useRef(null);
  useEffect(() => {
    if (autoFocus && htmlElRef.current) {
      htmlElRef.current.focus();
    }
  }, [autoFocus, htmlElRef.current]);

  const handleChange = ({ target: { value } }) => {
    onChange(value);
  };

  if (mask) {
    return (
      <InputMask
        maskChar="X"
        formatChars={{
          9: '[0-9]',
          A: '[А-Я]',
        }}
        mask={mask}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {(inputProps) => (
          <input
            {...inputProps}
            ref={htmlElRef}
            className={cn('modal-create-value', className)}
            style={style}
            placeholder={placeholder}
            autoFocus={autoFocus}
          />
        )}
      </InputMask>
    );
  } else {
    return (
      <input
        value={value}
        ref={htmlElRef}
        onChange={handleChange}
        className={cn('modal-create-value', className)}
        style={style}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  }
};

TableValueDynamic.propTypes = {
  err: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

TableValueDynamic.defaultProps = {
  err: false,
  className: '',
  autoFocus: false,
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
};

const TableValueDynamicAutoComplete = ({
  err,
  value,
  onChange,
  mask,
  placeholder,
  autoFocus,
  styles,
  historyParams,
  search,
}) => {
  const debounceValue = useDebounce(value, 100, true);
  const history = useHistory();

  const [result, setResult] = useState([]);
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    if (debounceValue && !debounceValue.includes('X')) {
      getClients(debounceValue);
    } else {
      setResult([]);
    }
  }, [debounceValue]);

  const handleClient = (client) => {
    history.push(`/client/${client.id}`, historyParams);
  };

  const getClients = async (value) => {
    const filters = {};
    filters[search] = value;
    const { data } = await clientApi.getClientList(filters);
    setResult(data);
  };

  const onFocus = () => {
    setFocus(true);
  };

  const onBlur = () => {
    setTimeout(() => {
      setFocus(false);
    }, 200);
  };

  const style = { ...styles, borderRadius: result.length > 0 ? '8px 8px 0 0' : '8px' };
  return (
    <div className="autocomplete-input">
      <TableValueDynamic
        err={err}
        value={value}
        onChange={onChange}
        mask={mask}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        styles={style}
      />
      {!!result.length && focus && (
        <div className="autocomplete-options scrollable">
          {result.map((elem) => {
            return (
              <div key={elem.id} className="autocomplete-item">
                <div className="contragent-select">
                  <Icon
                    name={
                      elem.isPendingDeletion
                        ? 'userverify-pending-deletition'
                        : elem.isTemporary
                        ? 'userverify-yellow'
                        : 'userverify'
                    }
                  />
                  <div className="contragent-data"> {elem.FIO}</div>
                </div>
                <div
                  className="button-case"
                  onClick={() => handleClient(elem)}
                  data-tip
                  data-for="client-profile-tooltip"
                >
                  <Icon name="usercard" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

TableValueDynamicAutoComplete.propTypes = {
  err: PropTypes.bool,
  value: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  historyParams: PropTypes.object,
};

TableValueDynamicAutoComplete.defaultProps = {
  err: false,
  autoFocus: false,
  onChange: () => {},
  historyParams: {},
};

const TableLabel = ({ icon, text, onClick }) => {
  return (
    <>
      <div></div>
      <div className={`modal-create-label is-rounded ${onClick ? 'is-active' : ''}`} onClick={onClick}>
        {icon && <Icon name={icon} style={{ display: 'inline-block', marginRight: '7px' }} />}
        {text}
      </div>
    </>
  );
};

TableLabel.propTypes = {
  icon: PropTypes.string,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  styles: PropTypes.object,
};

TableLabel.defaultProps = {
  icon: null,
  onClick: () => {},
  styles: {},
};

export { TableLabel, TableProp, TableValueDynamic, TableValueDynamicAutoComplete };
